import type { IOrderExportCmd } from "@/data/commands/order/OrderExportCmd";
import { Factory } from "@/data/dao/Factory";
import { OrderDao } from "@/data/dao/service/OrderDao";
import { DateTime } from "@/data/util/DateTime";



import { BaseModal } from "@/shared/elements/BaseModal";
import { logError } from "@/shared/logging/errors";
import { style } from "@/shared/ui/Style";



import { i18n } from "@/portal/Trans";
import { DatePicker } from "@/portal/formControls/DatePicker";
import { TimePicker } from "@/portal/formControls/TimePicker";
import { UiNotification } from "@/portal/ui/Notification";
import {DaoError} from "@/data/dao/service/BaseServiceDao";
import {exceptionTypes} from "@/data/util/Config";

export class OrderExportWindow extends BaseModal {
    private $form: JQuery;
    private $submitBtn: JQuery;
    
    private fromDatePicker: DatePicker;
    private fromTimePicker: TimePicker;
    private toDatePicker: DatePicker;
    private toTimePicker: TimePicker;

    constructor() {
        super(i18n("modal.orderExport.title"), "modal-lg", false);

        this.$element.addClass("orderexport");

        this.$form = $('<form class="kt-form kt-form--label-right" />').appendTo(this.$body);

        const createSection = $('<div class="kt-section kt-section--first"><div class="kt-section__body" /></div>')
            .appendTo(this.$form)
            .find("> .kt-section__body");
        createSection.append(
            '<div class="row"><label class="col-xl-4"></label><div class="col-lg-9 col-xl-6"><h3 class="kt-section__title kt-section__title-sm">' +
                i18n("modal.orderExport.settings") +
                "</h3></div></div>"
        );

        const endOfDay = new Date();
        endOfDay.setHours(23, 59, 59, 999);

        const midDay = DateTime.todayAsUtcMidnight();

        const fromGroup = $(
            '<div class="form-group row"><label class="col-xl-4 col-form-label">' +
                i18n("general.timeFormat.start") +
                ' <span class="mandatory">*</span></label><div class="col-xl-3 date"></div><div class="col-xl-3 time"></div></div>'
        ).appendTo(createSection);
        this.fromDatePicker = new DatePicker(undefined, { endDate: endOfDay })
            .setValue(midDay)
            .renderTo(fromGroup.find("> .col-xl-3.date"));
        this.fromTimePicker = new TimePicker(undefined, {
            minuteStep: 1,
            secondStep: 1,
            maxHours: 24,
            defaultTime: "00:00:00",
        }).renderTo(fromGroup.find("> .col-xl-3.time"));

        const toGroup = $(
            '<div class="form-group row"><label class="col-xl-4 col-form-label">' +
                i18n("general.timeFormat.end") +
                ' <span class="mandatory">*</span></label><div class="col-xl-3 date"></div><div class="col-xl-3 time"></div></div>'
        ).appendTo(createSection);
        this.toDatePicker = new DatePicker(undefined, { endDate: endOfDay }).setValue(midDay).renderTo(toGroup.find("> .col-xl-3.date"));
        this.toTimePicker = new TimePicker(undefined, {
            minuteStep: 1,
            secondStep: 1,
            maxHours: 24
        }).renderTo(toGroup.find("> .col-xl-3.time"));

        this.toTimePicker.setValue(DateTime.toTimeString(endOfDay, true));
        
        $('<button type="button" class="btn btn-secondary">' + i18n("general.cancel") + "</button>")
            .appendTo(this.$footer!)
            .click((e) => {
                e.preventDefault();
                this.close();
            });

        this.$submitBtn = $('<button type="button" class="btn btn-success">' + i18n("general.export") + "</button>")
            .appendTo(this.$footer!)
            .click((e) => {
                e.preventDefault();
                this.$form.submit();
            });

        this.$form.submit((e) => {
            e.preventDefault();
            e.stopPropagation();

            this.onExport();

            return false;
        });
    }

    protected onShow() {}

    protected onHide() {}

    private onExport() {

        const data: IOrderExportCmd = {
            from: this.fromDatePicker.getValue()!,
            to: this.toDatePicker.getValue(),
        };

        let hasError = false;
        this.$form.find(".is-invalid").removeClass("is-invalid");

        if (data.from === undefined) {
            this.fromDatePicker.setError(true);
            hasError = true;
        } else {
            let startTime = this.fromTimePicker.getSeconds();
            if (startTime === undefined) startTime = 0;

            data.from.setSeconds(startTime + data.from.getTimezoneOffset() * 60);
        }

        if (data.to === undefined) {
            this.toDatePicker.setError(true);
            hasError = true;
        } else {
            let endTime = this.toTimePicker.getSeconds();
            if (endTime === undefined) endTime = 0;

            data.to.setSeconds(endTime + data.to.getTimezoneOffset() * 60);
        }

        if (data.from >= data.to!) {
            this.fromDatePicker.setError(true);
            this.fromTimePicker.setError(true);
            this.toDatePicker.setError(true);
            this.toTimePicker.setError(true);
            hasError = true;
        }
        
        if (hasError) return;

        this.$submitBtn.addClass(style.buttonLoaderClass).prop("disabled", true).find("> .la").remove();
        Factory.getInstance<OrderDao>(OrderDao)
            .exportOrders(data)
            .then(() => {
                this.$submitBtn.removeClass(style.buttonLoaderClass).prop("disabled", false);
                this.close();
            })
            .catch((ex) => {
                logError(ex);
                this.$submitBtn.removeClass(style.buttonLoaderClass).prop("disabled", false);
                
                if (ex instanceof DaoError) {
                    if (ex.type === exceptionTypes.invalidOrderExcelDateRangeForOrderRoleException) {
                        UiNotification.showException(i18n("modal.orderExport.invalidDateRangeForOrder"));
                        return;
                    }
                }
                
                UiNotification.showException(i18n("modal.orderExport.failedExport"));

            });
    }
}
